/**
 * Бегущие подсказки в полях поиска шаблона (W-103): заранее заданные фразы по
 * очереди «печатаются» в атрибуте placeholder поля-плейсхолдера, затем
 * стираются. Фразы и скорости приходят из админки «TAS → Поиск: бегущие
 * подсказки» объектом window.TAS_SEARCH_PLACEHOLDER (blocks/footer.php);
 * объекта нет — поле показывает обычную надпись и таймеров не создаётся.
 *
 * Один таймер на страницу и одна общая строка на все поля (в шапке, в
 * фиксированной шапке и в меню-бургере их три) — иначе фразы разъезжались бы, а
 * таймеры множились. Набор идёт только когда есть хотя бы одно ВИДИМОЕ поле
 * (поле меню-бургера скрыто, пока меню закрыто) и останавливается: курсор в
 * любом поле, открытая панель поиска, скрытая вкладка, системный запрос
 * «меньше анимации» (prefers-reduced-motion).
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'ui.vue3';
import { searchState } from './search-state';

const DEFAULTS = {
    typeMs: 70,
    eraseMs: 30,
    holdMs: 1800,
    gapMs: 400,
    caret: true,
    shuffle: false,
};

/**
 * Пока ни одно поле не видно (меню-бургер закрыто, другая раскладка), набор
 * не идёт, но проверка повторяется с этим шагом: открытие меню и поворот
 * экрана событий в наш модуль не присылают.
 */
const IDLE_CHECK_MS = 1000;

/** то, что сейчас «набрано» (в паузе между фразами — пустая строка) */
const typed = ref('');
/**
 * Идёт ли набор. Отделено от текста: в паузе между фразами набранного текста
 * нет, но поле должно оставаться ПУСТЫМ, а не показывать надпись «Поиск» —
 * иначе она мелькает между фразами (замечание заказчика 2026-09-18, W-105).
 * Обычная надпись возвращается, только когда набор остановлен совсем.
 */
const running = ref(false);

let config = null;
let phrases = [];
let timer = null;
/** поля, которые сейчас на странице: элемент → живёт до размонтирования компонента */
const fields = new Set();
let focused = 0;
let started = false;

/** фраза, позиция курсора и фаза набора */
let index = 0;
let position = 0;
let erasing = false;

function readConfig() {
    const raw = typeof window !== 'undefined' ? window.TAS_SEARCH_PLACEHOLDER : null;
    if (!raw || !Array.isArray(raw.phrases)) {
        return null;
    }
    const list = raw.phrases.map((p) => String(p).trim()).filter(Boolean);
    if (!list.length) {
        return null;
    }
    return {
        phrases: list,
        typeMs: Number(raw.typeMs) > 0 ? Number(raw.typeMs) : DEFAULTS.typeMs,
        eraseMs: Number(raw.eraseMs) > 0 ? Number(raw.eraseMs) : DEFAULTS.eraseMs,
        holdMs: Number(raw.holdMs) > 0 ? Number(raw.holdMs) : DEFAULTS.holdMs,
        gapMs: Number(raw.gapMs) >= 0 ? Number(raw.gapMs) : DEFAULTS.gapMs,
        caret: raw.caret !== false,
        shuffle: raw.shuffle === true,
    };
}

function shuffled(list) {
    const copy = list.slice();
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

function reducedMotion() {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
        return false;
    }
}

/**
 * Есть ли на странице поле, которое посетитель действительно видит. Скрытые
 * поля шаблон не убирает из разметки, а уводит за экран (меню каталога сверху,
 * меню-бургер влево), поэтому нужен и прямоугольник в пределах окна, а не
 * только getClientRects: полей `.js-search-input` на странице четыре, из них
 * на десктопе видно одно.
 */
function hasVisibleField() {
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    for (const el of fields) {
        if (!el || el.getClientRects().length === 0) {
            continue;
        }
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < height && r.right > 0 && r.left < width) {
            return true;
        }
    }
    return false;
}

/**
 * Остановка по событию: курсор в поле, открытая панель, скрытая вкладка. Каждое
 * из трёх возвращает набор само (focusout, watch, visibilitychange), поэтому
 * таймер снимается совсем.
 */
function blockedByEvent() {
    return Boolean(focused) || searchState.open || document.hidden;
}

function stop() {
    clearTimeout(timer);
    timer = null;
    typed.value = '';
    running.value = false; // поле возвращает обычную надпись
}

/** Шаг набора: печать символа, пауза на готовой фразе, стирание, пауза между фразами */
function tick() {
    if (!config || blockedByEvent()) {
        stop();
        return;
    }
    if (!hasVisibleField()) {
        // поле скрыто — ждём его появления, начинаем фразу с начала и на это
        // время возвращаем обычную надпись (набора действительно нет)
        typed.value = '';
        running.value = false;
        position = 0;
        erasing = false;
        timer = setTimeout(tick, IDLE_CHECK_MS);
        return;
    }
    const phrase = phrases[index % phrases.length];
    // пауза между фразами оставляет поле пустым, а не с надписью «Поиск»
    running.value = true;
    typed.value = phrase.slice(0, position) + (config.caret ? '|' : '');

    let delay;
    if (!erasing && position < phrase.length) {
        position += 1;
        delay = config.typeMs;
    } else if (!erasing) {
        erasing = true;
        delay = config.holdMs;
    } else if (position > 0) {
        position -= 1;
        delay = config.eraseMs;
    } else {
        erasing = false;
        index = (index + 1) % phrases.length;
        delay = config.gapMs;
    }
    timer = setTimeout(tick, delay);
}

function restart() {
    clearTimeout(timer);
    if (!config || blockedByEvent()) {
        typed.value = '';
        running.value = false;
        timer = null;
        return;
    }
    timer = setTimeout(tick, 0);
}

function onVisibilityChange() {
    restart();
}

/** Первая подписка поднимает общий набор, последняя — снимает */
function start() {
    if (started) {
        return;
    }
    started = true;
    config = reducedMotion() ? null : readConfig();
    if (!config) {
        return;
    }
    phrases = config.shuffle ? shuffled(config.phrases) : config.phrases;
    index = 0;
    position = 0;
    erasing = false;
    document.addEventListener('visibilitychange', onVisibilityChange);
    watch(() => searchState.open, () => restart());
    restart();
}

function teardown() {
    if (!started || fields.size) {
        return;
    }
    started = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    stop();
}

/**
 * Подключение поля к общему набору.
 * @param {string} staticText надпись поля, когда набор стоит (обычный placeholder)
 * @returns {{placeholder, register, unregister, onFocus, onBlur}}
 */
export function useTypedPlaceholder(staticText) {
    const element = ref(null);

    /** поле уже могло появиться до старта — регистрируем элемент при монтировании */
    function register(el) {
        element.value = el || null;
        if (element.value) {
            fields.add(element.value);
            start();
            restart();
        }
    }

    function unregister() {
        if (element.value) {
            fields.delete(element.value);
            element.value = null;
        }
        teardown();
        restart();
    }

    function onFocus() {
        focused += 1;
        restart();
    }

    function onBlur() {
        focused = Math.max(0, focused - 1);
        restart();
    }

    onMounted(() => restart());
    onBeforeUnmount(() => unregister());

    return {
        placeholder: computed(() => (running.value ? typed.value : staticText)),
        register,
        unregister,
        onFocus,
        onBlur,
    };
}
